"use client";
import React, { useState, useMemo, useCallback } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import type { Id } from "../../../convex/_generated/dataModel";

interface Tag {
  _id: Id<"tag">;
  tag_name: string;
}

interface MultiSelectTagsProps {
  tags: Tag[];
  selectedTagIds: Id<"tag">[];
  onSelectionChange: (selectedIds: Id<"tag">[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiSelectTags({
  tags,
  selectedTagIds = [],
  onSelectionChange,
  placeholder = "Select tags...",
  searchPlaceholder = "Search tags...",
  emptyText = "No tags found.",
  disabled = false,
  className,
}: MultiSelectTagsProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const selectedTags = useMemo(
    () => tags.filter((tag: Tag) => selectedTagIds.includes(tag._id)),
    [tags, selectedTagIds],
  );

  const filteredTags = useMemo(() => {
    if (!search) return tags;
    return tags.filter((tag: Tag) =>
      tag.tag_name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [tags, search]);

  const handleSelect = useCallback(
    (tagId: Id<"tag">) => {
      const newSelection = selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((id: Id<"tag">) => id !== tagId)
        : [...selectedTagIds, tagId];
      onSelectionChange(newSelection);
    },
    [selectedTagIds, onSelectionChange],
  );

  const handleRemove = useCallback(
    (tagId: Id<"tag">) => {
      const newSelection = selectedTagIds.filter(
        (id: Id<"tag">) => id !== tagId,
      );
      onSelectionChange(newSelection);
    },
    [selectedTagIds, onSelectionChange],
  );

  const handleClearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between",
              selectedTags.length > 0 ? "text-black" : "text-muted-foreground",
              disabled && "cursor-not-allowed opacity-50",
            )}
            disabled={disabled}
          >
            <div className="flex flex-1 flex-wrap gap-1">
              {selectedTags.length === 0 ? (
                <span>{placeholder}</span>
              ) : selectedTags.length === 1 ? (
                <span>{selectedTags[0]?.tag_name}</span>
              ) : selectedTags.length <= 2 ? (
                selectedTags.map((tag: Tag) => (
                  <Badge key={tag._id} variant="secondary" className="text-xs">
                    {tag.tag_name}
                  </Badge>
                ))
              ) : (
                <span>{selectedTags.length} tags selected</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          container={containerRef.current}
          className="z-[70] w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-60">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {selectedTags.length > 0 && (
                  <div className="flex items-center justify-between p-2">
                    <span className="text-xs text-muted-foreground">
                      {selectedTags.length} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearAll}
                      className="h-auto p-1 text-xs"
                    >
                      Clear all
                    </Button>
                  </div>
                )}
                {filteredTags.map((tag: Tag) => {
                  const isSelected = selectedTagIds.includes(tag._id);
                  return (
                    <CommandItem
                      key={tag._id}
                      value={tag.tag_name}
                      onSelect={() => handleSelect(tag._id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className={cn(
                            "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible",
                          )}
                        >
                          <Check className="h-3 w-3" />
                        </div>
                        <span>{tag.tag_name}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* selected tags as removable badges */}
      {selectedTags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedTags.map((tag: Tag) => (
            <Badge
              key={tag._id}
              variant="secondary"
              className="flex items-center gap-1 text-xs"
            >
              {tag.tag_name}
              <button
                type="button"
                onClick={() => handleRemove(tag._id)}
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remove {tag.tag_name}</span>
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
